// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data: the page's side of one upload. Starts a Web Worker
 * (`ownDataUpload.worker.ts`) that does the reading, hashing, sealing and
 * sending, and ends it when the upload ends, which drops the dataset key
 * with the worker. Where there is no Worker (an old browser, the tests) the
 * same task runs on the page.
 */

import { UploadTask, defaultPutPart, type UploadCommand, type UploadEvent } from './ownDataUpload'

export interface UploadHandle {
  resume(): void
  cancel(): void
}

const FINAL: ReadonlyArray<UploadEvent['type']> = ['done', 'failed', 'cancelled']

/** Start uploading `file`; `onEvent` hears every step until done, failed or cancelled. */
export function startOwnDataUpload(file: File, title: string, onEvent: (e: UploadEvent) => void): UploadHandle {
  if (typeof Worker !== 'undefined') {
    let worker: Worker | null = new Worker(new URL('./ownDataUpload.worker.ts', import.meta.url), { type: 'module' })
    const send = (cmd: UploadCommand) => worker?.postMessage(cmd)
    worker.addEventListener('message', (e: MessageEvent<UploadEvent>) => {
      onEvent(e.data)
      if (FINAL.includes(e.data.type)) {
        worker?.terminate()
        worker = null
      }
    })
    worker.addEventListener('error', () => {
      onEvent({ type: 'failed', code: 'worker', status: null, job: null, dataset: null })
      worker?.terminate()
      worker = null
    })
    send({ type: 'start', file, title })
    return { resume: () => send({ type: 'resume' }), cancel: () => send({ type: 'cancel' }) }
  }
  const task = new UploadTask(onEvent, {
    fetch: (input, init) => fetch(input, init),
    putPart: defaultPutPart,
    now: () => Date.now(),
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  })
  task.handle({ type: 'start', file, title })
  return { resume: () => task.handle({ type: 'resume' }), cancel: () => task.handle({ type: 'cancel' }) }
}
